import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import withCSRF from '../../../commonComponents/withCSRF';
import translate from '../../../commonComponents/translate';
import {
  compose,
  handleImageUpload,
  INBOX_MSG_TYPE,
  INBOX_MSG_TYPES,
  SURVEY_QUESTION_TYPE,
} from '../../../services/utilityFunctions';
import TeamProp from '../../../commonComponents/propTypes/teamProp';
import Request from '../../../services/request';
import Notifications from '../../../services/notifications';
import FormInput from '../../../commonComponents/forms/FormInput';
import Form from '../../../commonComponents/forms/Form';
import FormHeader from '../../../commonComponents/forms/FormHeader';
import ImageInput from '../../../commonComponents/forms/ImageInput';
import FormFooter from '../../../commonComponents/forms/FormFooter';
import FormGroup from '../../../commonComponents/forms/FormGroup';
import FormLabel from '../../../commonComponents/forms/FormLabel';

function InboxMessageForm({ t, _csrf, type, teams }) {
  const isInfo = type === INBOX_MSG_TYPE.INFO;
  const isSurvey = type === INBOX_MSG_TYPE.SURVEY;
  const isEvent = type === INBOX_MSG_TYPE.EVENT;

  const [state, setState] = useState({
    type,
    title: '',
    info: '',
    owners: [],
    teams: [],
    url: '',
    image: { blob: null, fileName: '' },
  });

  const [surveyState, setSurveyState] = useState({
    type,
    startDate: null,
    endDate: null,
    question: '',
    answers: [
      { _id: '1', value: isEvent ? t('yes') : '' },
      { _id: '2', value: isEvent ? t('no') : '' },
    ],
  });

  useEffect(
    () => {
      const { startDate, endDate } = surveyState;
      if (startDate > endDate || endDate === null) {
        setSurveyState(prevState => ({ ...prevState, endDate: startDate }));
      }
    },
    [surveyState.startDate],
  );

  useEffect(
    () => {
      const { startDate, endDate } = surveyState;
      if (endDate < startDate) {
        setSurveyState(prevState => ({ ...prevState, startDate: endDate }));
      }
    },
    [surveyState.endDate],
  );

  const fixURL = () => {
    const { url } = state;
    if (
      url &&
      url.substr(0, 7) !== 'http://' &&
      url.substr(0, 8) !== 'https://'
    ) {
      setState(prevSate => ({ ...prevSate, url: `https://${url}` }));
    }
  };

  const validationRules = {
    title: { required: true },
    info: { required: true },
    url: { url: true },
    question: { required: true },
    answer_0: { required: true },
    answer_1: { required: true },
    endDate: { required: true },
  };

  const handleChange = field => value =>
    setState(prevState => ({ ...prevState, [field]: value }));

  const handleAddOption = field => value =>
    setState(prevState => ({
      ...prevState,
      [field]: prevState[field].concat(value),
    }));

  const handleRemoveOption = field => value =>
    setState(prevState => ({
      ...prevState,
      [field]: prevState[field].filter(v => v !== value),
    }));

  const handleSurveyChange = field => value =>
    setSurveyState(prevState => ({ ...prevState, [field]: value }));

  const handleAnswerChange = (answerId, value) =>
    setSurveyState(prevState => ({
      ...prevState,
      answers: prevState.answers.map(
        a => (a._id === answerId ? { _id: answerId, value } : a),
      ),
    }));

  const handleAddAnswer = () =>
    setSurveyState(prevState => ({
      ...prevState,
      answers: prevState.answers.concat({
        _id: (prevState.answers.length + 1).toString(),
        value: '',
      }),
    }));

  const handleRemoveAnswer = answerId =>
    setSurveyState(prevState => ({
      ...prevState,
      answers: prevState.answers.filter(v => v._id !== answerId),
    }));

  const handleCreateSurvey = image =>
    new Promise(resolve => {
      const params = {
        title: state.title,
        image,
        endDate: surveyState.endDate,
        questions: [
          {
            type: isSurvey
              ? SURVEY_QUESTION_TYPE.MULTIPLE_ANSWERS
              : SURVEY_QUESTION_TYPE.OPTIONS,
            question: surveyState.question,
            image,
            options: surveyState.answers.map(a => ({ option: a.value })),
          },
        ],
        type,
        startDate: isEvent ? surveyState.startDate : undefined,
      };

      new Request(_csrf)
        .post('/survey', params)
        .done(({ data }) =>
          resolve({
            survey: data._id,
            info: surveyState.question,
          }),
        )
        .fail(err => {
          Notifications.showErrorFromRequest(err, 'error_inboxMessageCreate');
        });
    });

  const handleSubmit = async () => {
    const image = await handleImageUpload(t, _csrf, state.image);

    let { info, survey } = state;
    if (isEvent || isSurvey) {
      ({ info, survey } = await handleCreateSurvey(image));
    }

    const params = {
      ...state,
      info,
      survey,
      owners: state.owners.length > 0 ? state.owners : undefined,
      teams: state.teams.length > 0 ? state.teams : undefined,
      url: state.url || undefined,
      image,
    };

    new Request(_csrf)
      .post('/dashboard', params)
      .done(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('success_create'),
        );
        window.location.href = '/dashboard';
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_inboxMessageCreate');
      });
  };

  const createInput = (field, typeParam, required) => {
    const inputType = typeParam || 'text';

    return (
      <FormInput
        id={field}
        name={field}
        value={state[field]}
        onChange={handleChange(field)}
        label={t(`inboxDetails_${field}`)}
        placeholder={t(`inboxDetails_${field}Placeholder`)}
        labelClassName="col-2"
        containerClassName="col-7"
        type={inputType}
        groupProps={{ className: 'row' }}
        inputProps={{
          required,
          rows: typeParam === 'textarea' ? 5 : undefined,
          onBlur: field === 'url' ? fixURL : undefined,
        }}
      />
    );
  };

  const createSelect = (field, items) => (
    <FormInput
      id={field}
      name={field}
      value={state[field]}
      onChange={handleAddOption(field)}
      label={t(`inboxDetails_${field}`)}
      labelClassName="col-2"
      containerClassName="col-7"
      type={field === 'owners' ? 'users' : 'select2'}
      placeholder={t(`inboxDetails_${field}Placeholder`)}
      hint={field === 'teams' ? t('inboxDetails_teamsHint') : undefined}
      groupProps={{ className: 'row' }}
      inputProps={{
        onUnselect: handleRemoveOption(field),
        width: '100%',
        multiple: true,
        items,
      }}
    />
  );

  const imagePlaceholder =
    'https://www.placehold.it/350x170/EFEFEF/AAAAAA&amp;text=no+image';

  const answerBtnClass = cx(
    'btn',
    'm-btn',
    'm-btn--hover-danger',
    'm-btn--icon',
    'm-btn--icon-only',
    'm-btn--pill',
    'remove-answer-button',
  );

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id="inboxForm"
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <FormHeader title={`${t('create')} ${t(`inbox_type_${type}`)}`} />
            <ImageInput
              onChange={handleChange('image')}
              label={t('inboxDetails_image')}
              placeholder={imagePlaceholder}
              inputProps={{
                cropLocked: true,
                cropHeight: 170,
                cropWidth: 350,
              }}
            />
            {createInput('title', 'text', true)}
            {isInfo && (
              <>
                {createSelect('owners')}
                {createSelect(
                  'teams',
                  teams.map(te => ({ id: te._id, text: te.name })),
                )}
                {createInput('info', 'textarea')}
              </>
            )}
            {(isInfo || isEvent) && createInput('url')}
            {(isSurvey || isEvent) && (
              <>
                {isEvent && (
                  <FormInput
                    id="startDate"
                    name="startDate"
                    value={surveyState.startDate}
                    onChange={handleSurveyChange('startDate')}
                    label={t('inboxDetails_startDate')}
                    placeholder={t('inboxDetails_startDatePlaceholder')}
                    labelClassName="col-2"
                    containerClassName="col-7"
                    type="date"
                    groupProps={{ className: 'row' }}
                  />
                )}
                <FormInput
                  id="endDate"
                  name="endDate"
                  value={surveyState.endDate}
                  onChange={handleSurveyChange('endDate')}
                  label={t('inboxDetails_endDate')}
                  placeholder={t('inboxDetails_endDatePlaceholder')}
                  labelClassName="col-2"
                  containerClassName="col-7"
                  type="date"
                  groupProps={{ className: 'row' }}
                  inputProps={{ required: true }}
                />
                <FormInput
                  id="question"
                  name="question"
                  value={surveyState.question}
                  onChange={handleSurveyChange('question')}
                  label={t('inboxDetails_question')}
                  placeholder={t('inboxDetails_questionPlaceholder')}
                  labelClassName="col-2"
                  containerClassName="col-7"
                  type="text"
                  groupProps={{ className: 'row' }}
                  inputProps={{ required: true }}
                />
                <div className="inbox-survey-answers">
                  {surveyState.answers.map((answer, index) => (
                    <FormGroup key={answer._id} className="row">
                      <FormLabel
                        id={answer._id}
                        label={t('inboxDetails_answers')}
                        className="col-2"
                      />
                      <div className="col-7">
                        <input
                          id={answer._id}
                          name={`answer_${index}`}
                          className="form-control m-input"
                          type="text"
                          placeholder={t(
                            `inboxDetails_answer${index + 1}Placeholder`,
                          )}
                          value={answer.value}
                          onChange={({ currentTarget }) =>
                            handleAnswerChange(answer._id, currentTarget.value)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className={answerBtnClass}
                        title={t('edit')}
                        onClick={() => handleRemoveAnswer(answer._id)}
                      >
                        <i className="la la-trash" />
                      </button>
                    </FormGroup>
                  ))}
                  {surveyState.answers.length < 5 && (
                    <div className="form-group m-form__group row">
                      <div className="col-7 offset-2">
                        <button
                          className="btn btn-outline-dynamic m-btn m-btn--icon"
                          type="button"
                          onClick={handleAddAnswer}
                        >
                          <span>
                            <i className="la la-plus" />
                            <span>{t('inboxDetails_addAnswer')}</span>
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

InboxMessageForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  type: PropTypes.oneOf(INBOX_MSG_TYPES).isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape(TeamProp.propType)).isRequired,
};

export default compose(
  translate,
  withCSRF,
)(InboxMessageForm);
